import Checkbox from '@eg/elements/Checkbox';
import ControlWithHint from '@eg/elements/ControlWithHint';
import CheckIcon from '@eg/elements/Icons/CheckIcon';
import { LoaderIcon } from '@eg/elements/components/Icons';
import { ChangeEvent, useEffect, useState } from 'react';
import AemFragment from '../../../../components/AemFragment/AemFragment';
import { AppLoaderBlack } from '../../../../components/AppLoader/AppLoaderBlack';
import { ErrorModal } from '../../../../components/ErrorModal/ErrorModal';
import Footer, { AuxButton } from '../../../../components/Footer';
import { Headline } from '../../../../components/Headline/Headline';
import Text from '../../../../components/Text';
import { CONFIG_BACKEND_API_PREFIX, CONFIG_BACKEND_BASE_URL } from '../../../../config';
import * as downloadPDFUtils from '../../../../helpers/downloadPDF.utils';
import { NavigationAction, StateName } from '../../../../routing/StateMachineTypes';
import { Tracker } from '../../../../tracking/tracker';
import { TrackingElementIDD } from '../../../../tracking/tracking.types';
import { AemFragmentCatalog } from '../../../../types/AemFragmentCatalog';
import { DataIdFeePage } from '../../../../types/DataId';
import { SharedSubmitCallBack } from '../../../../types/callbacks/sharedSubmitCallBack';
import './IddDownloadPdf.css';

export const COPY = {
  headline: 'Sichern Sie jetzt Ihren Antrag.',
  subline: 'Prüfen Sie das Dokument, bevor Sie unterschreiben.',
  checkBox:
    'Ich verzichte für diesen Vertragsabschluss auf eine persönliche Beratung sowie deren Dokumentation. Dies kann sich evtl. auf die Geltendmachung von Schadensersatzansprüchen nachteilig auswirken.',
};

interface IddDownloadPdfProps extends SharedSubmitCallBack<string> {
  onNavigateNoSubmit: (navigationRequest: NavigationAction) => void;
  onSubmit: (value: string, navigationRequest: NavigationAction) => void;
  businessId: string;
  onError: (e: Error) => void;
  pdfUrlDownloaded: string | undefined;
  trackClickEvent: (clickedElement: TrackingElementIDD) => void;
}

export const IddDownloadPdf = ({
  businessId,
  onError,
  onSubmit,
  onNavigateNoSubmit,
  trackClickEvent,
  pdfUrlDownloaded,
}: IddDownloadPdfProps) => {
  const [isSubmitOrderButtonDisabled, setIsSubmitOrderButtonDisabled] = useState(!pdfUrlDownloaded);
  const [isDownloadPdfButtonDisabled, setIsDownloadPdfButtonDisabled] = useState(!pdfUrlDownloaded);
  const [isWaiverAccepted, setIsWaiverAccepted] = useState(!!pdfUrlDownloaded);
  const [isPdfDownloaded, setIsPdfDownloaded] = useState(!!pdfUrlDownloaded);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [showError, setShowError] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);

  useEffect(() => {
    downloadPDFUtils.setPdfDownloaded(false);
  }, []);

  const handleCheckboxChange = ({ target: { checked } }: ChangeEvent<HTMLInputElement>): void => {
    trackClickEvent(TrackingElementIDD.CHECKBOX_IDD);
    setIsWaiverAccepted(checked);
    setIsDownloadPdfButtonDisabled(!checked);
    setIsPdfDownloaded(false);
    setIsSubmitOrderButtonDisabled(true);
    setShowError(!checked);
    if (!checked) {
      downloadPDFUtils.setPdfDownloaded(false);
    }
  };

  const onDownloadPdfClick = async (): Promise<void> => {
    trackClickEvent(TrackingElementIDD.BUTTON_IDD_DOWNLOAD);
    setOpenErrorModal(false);

    try {
      setIsLoadingPdf(true);

      const dynamicPdfUrl = `${CONFIG_BACKEND_BASE_URL}${CONFIG_BACKEND_API_PREFIX}/document/${businessId}/create`;
      await downloadPDFUtils.openPDFByBlob(dynamicPdfUrl, 'Vertragsdokumente_RLV_DIVE', 'POST');

      setIsPdfDownloaded(true);
      setIsSubmitOrderButtonDisabled(false);
    } catch (err) {
      setOpenErrorModal(true);
      onError(err as Error);
    } finally {
      setIsLoadingPdf(false);
    }
  };

  return (
    <div className="downloadpdf content-container">
      <Headline text={COPY.headline} subline={COPY.subline} />
      <div className="form-wrapper">
        <div className="form-row">
          <Text styles="bold">
            Für Vertragsabschlüsse per Internet gilt: Beratungspflicht (EU-Vorgabe)
          </Text>
          <Text>
            ERGO muss Sie beraten und dies dokumentieren. Oder Sie schließen ohne Beratung ab -
            stimmen Sie dazu einfach dem Beratungsverzicht zu.
          </Text>
        </div>
        <hr />
        <div className="form-row">
          <ControlWithHint
            className="form-row__error"
            error={
              showError
                ? 'Bitte bestätigen Sie, dass Sie für diesen Vertragsabschluss auf eine persönliche Beratung verzichten.'
                : ''
            }
          >
            <Checkbox
              label={COPY.checkBox}
              checked={isWaiverAccepted}
              onChange={handleCheckboxChange}
              data-component-id={DataIdFeePage.INPUT_CHECKBOX}
              verticalAlign="top"
            />
          </ControlWithHint>
        </div>
      </div>
      <Text styles="bold subheader">Vertragsunterlagen herunterladen</Text>
      <Text>
        Bitte speichern Sie vor dem Unterschreiben die Vertragsunterlagen und Informationen als PDF
        und prüfen diese auf Richtigkeit.
      </Text>
      <AuxButton
        key="1"
        step={1}
        label="Unterlagen herunterladen"
        disabled={isDownloadPdfButtonDisabled}
        onClick={onDownloadPdfClick}
        iconRight={
          (isPdfDownloaded && CheckIcon) ||
          (!isPdfDownloaded && isLoadingPdf && LoaderIcon) ||
          undefined
        }
      />
      <AemFragment name={AemFragmentCatalog.STEP22VA} useVA={true} />
      <Footer
        labelNext="weiter zur Unterschrift"
        handleNext={() => {
          trackClickEvent(TrackingElementIDD.BUTTON_CONTINUE_WITH_SIGNATURE);
          onSubmit('', NavigationAction.NEXT);
        }}
        handleAction={(navigationAction: NavigationAction) => {
          if (navigationAction === NavigationAction.BACK) {
            downloadPDFUtils.setPdfDownloaded(false);
            onNavigateNoSubmit(NavigationAction.BACK);
          }
        }}
        showNext={true}
        showPrevious={true}
        stepNext={2}
        disableNext={isSubmitOrderButtonDisabled}
      />

      <ErrorModal open={openErrorModal} businessId={businessId} onClick={onDownloadPdfClick} />
      <AppLoaderBlack
        show={isLoadingPdf}
        firstLine="Moment bitte ..."
        secondLine="Die Verarbeitung Ihrer Daten kann bis zu 20 Sekunden dauern."
      />
    </div>
  );
};
